import { useMemo } from 'react';
import { CampaignType_Enum as CampaignType } from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/v2';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { useNavigate } from '@frontend/weave-router';
import { NEW_CAMPAIGN_ID } from '../constants';
import type { EditorView } from '../types';
import toSearchFlag from '../utils/to-search-flag';
import toSearchParam from '../utils/to-search-param';
import { useBulkUrlData } from './use-bulk-url-data';

type BulkNavigatorEditorProps = {
  /** The ID of the campaign to navigate to. Omit or use NEW_CAMPAIGN_ID to create a new campaign. */
  campaignId?: string;
  /** True to copy the audience from the seed campaign when duplicating a campaign */
  isCopyingAudience?: boolean;
  /** True to create an audience segment using recipients passed from another context */
  isUsingPassedRecipients?: boolean;
  /** The ID of the campaign to use as a seed campaign when duplicating a campaign */
  seedCampaignId?: string;
};

type BulkNavigatorComposerProps = BulkNavigatorEditorProps & {
  /** True if the email composer should edit the loaded email template */
  isEditingTemplate?: boolean;
  /** True if the email composer will load a system email template */
  isSystemTemplate?: boolean;
  /** True if the email composer should start with a blank canvas instead of loading the existing document */
  startFresh?: boolean;
  /** The ID of the email template to use in the email composer */
  templateId?: string;
};

/**
 * Hook that provides navigation functions for bulk messaging features.
 *
 * @param campaignType - The feature to provide navigation for. Defaults to EMAIL.
 * @returns An object with bulk messaging navigation functions.
 */
export const useBulkNavigator = (campaignType?: CampaignType) => {
  const {
    campaignId: urlCampaignId,
    isCopyingAudience: urlIsCopyingAudience,
    isEditingTemplate: urlIsEditingTemplate,
    isSystemTemplate: urlIsSystemTemplate,
    isUsingPassedRecipients: urlIsUsingPassedRecipients,
    seedCampaignId: urlSeedCampaignId,
    startFresh: urlStartFresh,
    subNav = 'all-campaigns',
    templateId: urlTemplateId,
  } = useBulkUrlData();

  const navigate = useNavigate();
  const { navigate: settingsNavigate } = useSettingsNavigate();
  const type = campaignType === CampaignType.SMS ? 'text' : 'email';

  return useMemo(() => {
    const editorNavigate = (
      view: EditorView,
      {
        campaignId = urlCampaignId ?? NEW_CAMPAIGN_ID,
        isCopyingAudience,
        isEditingTemplate,
        isSystemTemplate,
        isUsingPassedRecipients,
        seedCampaignId,
        startFresh,
        templateId,
      }: BulkNavigatorComposerProps = {}
    ) => {
      const composeSearch =
        view === 'compose'
          ? {
              isEdit: toSearchFlag(isEditingTemplate, urlIsEditingTemplate),
              isSystemTemplate: toSearchFlag(isSystemTemplate, urlIsSystemTemplate),
              startFresh: toSearchFlag(startFresh, urlStartFresh),
              templateId: toSearchParam(templateId, urlTemplateId),
            }
          : {};

      return navigate({
        to: '/bulk/$subNav/$type/$id/$view',
        params: {
          id: campaignId,
          subNav,
          type,
          view,
        },
        search: {
          copyAudience: toSearchFlag(isCopyingAudience, urlIsCopyingAudience),
          seedId: toSearchParam(seedCampaignId, urlSeedCampaignId),
          usePassedRecipients: toSearchFlag(isUsingPassedRecipients, urlIsUsingPassedRecipients),
          ...composeSearch,
        },
      });
    };

    const generateEditorNavigate = (view: EditorView) => {
      return (editorProps?: BulkNavigatorEditorProps) => {
        return editorNavigate(view, editorProps);
      };
    };

    return {
      /**
       * Navigate to the campaigns list
       */
      campaigns: (feature?: CampaignType) => {
        if (feature || campaignType) {
          return navigate({
            to: '/bulk/$subNav/$type',
            params: {
              subNav: 'all-campaigns',
              type: (feature ?? campaignType) === CampaignType.SMS ? 'text' : 'email',
            },
          });
        }
        return navigate({ to: '/bulk/$subNav', params: { subNav: 'all-campaigns' } });
      },

      /**
       * Navigate to the drafts list
       */
      drafts: () => navigate({ to: '/bulk/$subNav', params: { subNav: 'drafts' } }),

      /**
       * Navigate to the email composer
       */
      composer: (composerProps?: BulkNavigatorComposerProps) => editorNavigate('compose', composerProps),

      /**
       * Navigate to the campaign editor
       */
      editor: generateEditorNavigate('editor'),

      /**
       * Navigate to the email settings page
       */
      settings: () => settingsNavigate({ to: '/email' }),

      /**
       * Navigate to the email template picker
       */
      templates: generateEditorNavigate('templates'),

      /**
       * Navigate to the email composer's welcome page
       */
      new: generateEditorNavigate('new-email'),

      /**
       * Navigate to the subscriptions settings page
       */
      subscriptions: () => settingsNavigate({ to: '/subscriptions/feature/:id', params: { id: 'bulk-messaging' } }),
    };
  }, [
    campaignType,
    navigate,
    settingsNavigate,
    subNav,
    type,
    urlCampaignId,
    urlIsCopyingAudience,
    urlIsEditingTemplate,
    urlIsSystemTemplate,
    urlIsUsingPassedRecipients,
    urlSeedCampaignId,
    urlStartFresh,
    urlTemplateId,
  ]);
};
